import { type ReactNode } from "react";
import { FileUp, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useFilePicker } from "./useFilePicker";
import type { FileRejection, FileValidator } from "./types";

export interface FileDropzoneProps {
  accept?: string;
  multiple?: boolean;
  validate?: FileValidator;
  onFilesAdmitted: (files: File[]) => void;
  onRejected?: (rejections: FileRejection[]) => void;
  isBusy?: boolean;
  disabled?: boolean;
  label?: string;
  hint?: string;
  browseLabel?: string;
  icon?: ReactNode;
  className?: string;
}

// Same useFilePicker underneath as FileUploadButton — only the presentation
// differs, so validation/rejection/busy semantics stay identical between them.
export function FileDropzone({
  accept,
  multiple,
  validate,
  onFilesAdmitted,
  onRejected,
  isBusy,
  disabled,
  label = "Drop files here or click to browse",
  hint,
  browseLabel = "Browse Files",
  icon,
  className,
}: FileDropzoneProps) {
  const picker = useFilePicker({ accept, multiple, validate, onFilesAdmitted, onRejected, disabled: disabled || isBusy });
  const busy = isBusy || picker.isValidating;

  return (
    <div
      className={cn(
        "w-full border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer",
        picker.isDragActive ? "border-brand-teal bg-brand-teal/5" : "border-muted-foreground/30 hover:border-brand-teal/50",
        (disabled || busy) && "pointer-events-none opacity-60",
        className
      )}
      {...picker.dropzoneProps}
      onClick={picker.openFilePicker}
    >
      <input {...picker.inputProps} />
      {icon ?? <FileUp className="w-10 h-10 text-muted-foreground mx-auto mb-3" />}
      <p className="text-sm font-medium text-foreground mb-1">{label}</p>
      {hint && <p className="text-xs text-muted-foreground mb-3">{hint}</p>}
      <Button
        type="button"
        variant="brand"
        size="sm"
        className="gap-1.5"
        disabled={disabled || busy}
        onClick={(e) => { e.stopPropagation(); picker.openFilePicker(); }}
      >
        <Upload className="w-4 h-4" /> {busy ? "Uploading…" : browseLabel}
      </Button>
    </div>
  );
}
